import { computed } from 'vue'
import { defineStore } from 'pinia'
import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import type { SmsTemplate, TemplateCategory } from '@farmfree/common/types/template/smsTemplate'
import { TemplateType } from '@farmfree/common/types/template/smsTemplate'
import { createSmsTemplateService } from '@/services'

const baseSmsTemplateStore = createIndexedDBStore<SmsTemplate>({
  storeId: 'smsTemplate',
  tableName: 'sms-templates',
  versionField: 'smsTemplateVersion',
  service: createSmsTemplateService('admin'),
})

export const useSmsTemplateStore = defineStore('smsTemplateExtended', () => {
  const baseStore = baseSmsTemplateStore()

  const templatesByCategory = computed(() => {
    const grouped: { [key: string]: SmsTemplate[] } = {}
    baseStore.items.forEach((template: SmsTemplate) => {
      if (!grouped[template.category]) {
        grouped[template.category] = []
      }
      grouped[template.category].push(template)
    })
    return grouped
  })

  const activeTemplates = computed<SmsTemplate[]>(() => {
    return baseStore.items.filter((t: SmsTemplate) => t.isActive)
  })

  const activeTemplatesByCategory = computed(() => {
    const grouped: { [key: string]: SmsTemplate[] } = {}
    activeTemplates.value.forEach((template: SmsTemplate) => {
      if (!grouped[template.category]) {
        grouped[template.category] = []
      }
      grouped[template.category].push(template)
    })
    return grouped
  })

  const getRandomActiveTemplate = (
    category: TemplateCategory,
    type: TemplateType = TemplateType.DELIVERY
  ): SmsTemplate | null => {
    const activeTemplates = baseStore.items.filter(
      (t) => t.category === category && t.type === type && t.isActive
    )
    if (activeTemplates.length === 0) return null

    const randomIndex = Math.floor(Math.random() * activeTemplates.length)
    return activeTemplates[randomIndex]
  }

  const getTemplatesByCategory = (category: TemplateCategory): SmsTemplate[] => {
    return templatesByCategory.value[category] || []
  }

  const getActiveTemplatesByCategory = (category: TemplateCategory): SmsTemplate[] => {
    return activeTemplatesByCategory.value[category] || []
  }

  return {
    ...baseStore,
    templatesByCategory,
    activeTemplates,
    activeTemplatesByCategory,
    getRandomActiveTemplate,
    getTemplatesByCategory,
    getActiveTemplatesByCategory,
  }
})
